import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ─── White Institutional Theme — Navy Primary ────────────────────────────────

const PAGE_BG = "#EEF2F8";
const CARD    = "#FFFFFF";
const BORDER  = "#C8D4E8";
const SHADOW  = "0 1px 4px rgba(0,32,96,0.08), 0 0 0 1px rgba(0,32,96,0.06)";

const T1 = "#002060";   // brand navy — headings
const T2      = "#0a3270";   // body text
const T3      = "#1a4a9a";   // labels (dark navy — no gray)

const TEAL   = "#0284C7";
const PURPLE = "#6D28D9";
const AMBER  = "#D97706";
const GREEN  = "#059669";
const RED    = "#DC2626";
const BLUE   = "#1D4ED8";

const BAR_GRAD  = `linear-gradient(90deg, ${TEAL}, ${PURPLE})`;
const BAR_TRACK = "#D6E0F0";

const FONT = "'Inter','DM Sans','Segoe UI',sans-serif";

// ─── Types ────────────────────────────────────────────────────────────────────

interface VM { value: any; description?: string; use?: string }

interface QDD {
  dashboard: {
    meta: { company_name: VM; ticker: VM; strategy_type: VM; holding_period: VM };
    hero_section: {
      company_name: VM; ticker: VM; current_price: VM; daily_return_pct: VM;
      signal: VM; technical_score: VM; event_grade: VM;
      ai_trend_status: { description?: string; status: VM; signal: VM; confidence: VM };
      momentum_status: { description?: string; status: VM; score: VM };
      relative_strength: { description?: string; status: VM; score: VM };
    };
    trend_price_structure: {
      description?: string;
      analysis: { open: VM; high: VM; low: VM; close: VM; new_20d_high: VM; new_52w_high: VM };
      signals: {
        trend_status: VM; bullish_bearish_bias: VM;
        trend_continuation_score: VM; moving_average_alignment_quality: VM; breakout_quality: VM;
      };
    };
    volume_participation: {
      description?: string;
      signals: {
        participation_quality: VM; liquidity_quality: VM;
        smart_money_activity: VM; accumulation_distribution_bias: VM;
      };
    };
    momentum: {
      description?: string;
      signals: {
        momentum_status: VM; breakout_continuation_quality: VM;
        acceleration_score: VM; pullback_quality: VM; persistence_score: VM;
      };
    };
    volatility_risk: {
      description?: string;
      signals: {
        risk_score: VM; stabilization_score: VM; volatility_heat: VM;
        speculative_activity_status: VM; risk_adjusted_momentum: VM;
      };
    };
    key_technical_levels: {
      description?: string;
      signals: { breakout_trigger: VM; resistance_levels: VM; support_levels: VM; trend_support_quality: VM };
    };
    event_quality_engine: {
      description?: string;
      scores: {
        overall_score: VM; institutional_grade: VM; momentum_score: VM;
        liquidity_score: VM; risk_score: VM; stability_score: VM;
      };
      institutional_summary: { ai_conviction: VM; summary: VM; trading_bias: VM; ideal_setup: VM };
    };
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const isQuantDashboardFormat = (data: any): boolean =>
  data !== null && typeof data === "object" && !Array.isArray(data) &&
  "dashboard" in data && "hero_section" in (data.dashboard || {});

const sigCol = (v: string = "") => {
  const s = v.toLowerCase();
  if (s.includes("buy") || (s.includes("bullish") && !s.includes("speculative"))) return GREEN;
  if (s.includes("avoid") || s.includes("sell") || s.includes("bearish"))         return RED;
  if (s.includes("momentum") || s.includes("acceleration") || s.includes("outperform")) return TEAL;
  if (s.includes("risk") || s.includes("speculative") || s.includes("volatile") || s.includes("extreme")) return AMBER;
  if (s.includes("strong") || s.includes("high velocity")) return GREEN;
  return PURPLE;
};

const scCol = (n: number) => n >= 70 ? GREEN : n >= 50 ? TEAL : n >= 30 ? AMBER : RED;
const fmt   = (v: number) => typeof v === "number" ? v.toFixed(2) : String(v);

// ─── InfoTooltip ──────────────────────────────────────────────────────────────

const Info: React.FC<{ d?: string; u?: string }> = ({ d, u }) => {
  if (!d && !u) return null;
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5, maxWidth: 250 }}>
          {d && <Typography sx={{ fontSize: "0.72rem", color: "#D8E8F8", lineHeight: 1.55, mb: u ? 0.7 : 0 }}>{d}</Typography>}
          {u && (
            <Box sx={{ borderTop: d ? "1px solid rgba(255,255,255,0.1)" : "none", pt: d ? 0.7 : 0 }}>
              <Typography sx={{ fontSize: "0.65rem", color: "#8BA4BF", lineHeight: 1.5 }}>
                <Box component="span" sx={{ color: TEAL, fontWeight: 700 }}>USE · </Box>{u}
              </Typography>
            </Box>
          )}
        </Box>
      }
      componentsProps={{
        tooltip: { sx: { background: "#0F1C30", border: "1px solid #1E3A58", borderRadius: 2, p: 1.2, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" } },
        arrow:   { sx: { color: "#0F1C30" } },
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: "0.8rem", color: T3, cursor: "help", ml: 0.5, flexShrink: 0, opacity: 0.6, "&:hover": { color: BLUE, opacity: 1 }, transition: "all 0.15s" }} />
    </Tooltip>
  );
};

// ─── Gradient Progress Bar ────────────────────────────────────────────────────

const GradBar: React.FC<{ label: string; value: number; d?: string; u?: string }> = ({ label, value, d, u }) => (
  <Box sx={{ mb: 1.8 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: "0.78rem", color: T1, fontWeight: 600, fontFamily: FONT }}>{label}</Typography>
        <Info d={d} u={u} />
      </Box>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: scCol(value), fontFamily: "'SF Mono','Fira Code',monospace" }}>
        {value}
      </Typography>
    </Box>
    <Box sx={{ height: 6, background: BAR_TRACK, borderRadius: 99 }}>
      <Box sx={{ width: `${Math.min(Math.max(value, 0), 100)}%`, height: "100%", background: BAR_GRAD, borderRadius: 99 }} />
    </Box>
  </Box>
);

// ─── Row label-value ──────────────────────────────────────────────────────────

const DataRow: React.FC<{ label: string; value: string; color?: string; d?: string; u?: string }> = ({ label, value, color = T1, d, u }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, py: 1, borderBottom: `1px solid ${BORDER}` }}>
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography sx={{ fontSize: "0.75rem", color: T2, fontWeight: 600, fontFamily: FONT }}>{label}</Typography>
      <Info d={d} u={u} />
    </Box>
    <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color, textAlign: "right", lineHeight: 1.3, fontFamily: FONT }}>{value}</Typography>
  </Box>
);

// ─── Section card ─────────────────────────────────────────────────────────────

const Card: React.FC<{ title: string; subtitle?: string; badge?: string; badgeColor?: string; desc?: string; children: React.ReactNode }> = ({
  title, subtitle, badge, badgeColor = TEAL, desc, children,
}) => (
  <Box sx={{ background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: SHADOW, overflow: "hidden", height: "100%" }}>
    <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${BORDER}` }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: T1, fontFamily: FONT }}>{title}</Typography>
            {desc && <Info d={desc} />}
          </Box>
          {subtitle && (
            <Typography sx={{ fontSize: "0.73rem", color: T2, fontWeight: 500, mt: 0.2, fontFamily: FONT }}>{subtitle}</Typography>
          )}
        </Box>
        {badge && (
          <Box sx={{ px: 1.5, py: 0.35, borderRadius: 99, background: `${badgeColor}14`, border: `1px solid ${badgeColor}40`, flexShrink: 0, mt: 0.2 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: badgeColor, fontFamily: FONT, letterSpacing: "0.03em" }}>{badge}</Typography>
          </Box>
        )}
      </Box>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ─── Big Metric card (row of 4) ───────────────────────────────────────────────

const MetricCard: React.FC<{ label: string; value: string | number; sub: string; color: string; d?: string; u?: string }> = ({
  label, value, sub, color, d, u,
}) => (
  <Box sx={{ background: CARD, borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: SHADOW, p: 2.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      <Typography sx={{ fontSize: "0.72rem", color: T2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: FONT }}>
        {label}
      </Typography>
      <Info d={d} u={u} />
    </Box>
    <Typography sx={{ fontSize: "2.4rem", fontWeight: 800, color, lineHeight: 1, mb: 1.5, fontFamily: FONT, letterSpacing: "-0.02em" }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "0.75rem", color: T1, fontWeight: 500, fontFamily: FONT }}>{sub}</Typography>
  </Box>
);

// ─── OHLC Cell ────────────────────────────────────────────────────────────────

const OHLCItem: React.FC<{ label: string; v: VM }> = ({ label, v }) => (
  <Box sx={{ textAlign: "center", p: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
      <Typography sx={{ fontSize: "0.63rem", color: T2, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 700, fontFamily: FONT }}>
        {label}
      </Typography>
      <Info d={v.description} u={v.use} />
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: T1, fontFamily: "'SF Mono','Fira Code',monospace" }}>
      ${fmt(v.value)}
    </Typography>
  </Box>
);

// ─── Level pill ───────────────────────────────────────────────────────────────

const Level: React.FC<{ value: number; color: string; sub?: string }> = ({ value, color, sub }) => (
  <Box sx={{ background: `${color}0C`, border: `1px solid ${color}33`, borderRadius: 2, p: 1.5, textAlign: "center", mb: 0.8 }}>
    {sub && <Typography sx={{ fontSize: "0.58rem", color, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.3, fontFamily: FONT }}>{sub}</Typography>}
    <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color, fontFamily: "'SF Mono','Fira Code',monospace" }}>${fmt(value)}</Typography>
  </Box>
);

// ─── Main ─────────────────────────────────────────────────────────────────────

const QuantDashboardRenderer: React.FC<{ data: QDD }> = ({ data }) => {
  const d  = data.dashboard;
  const h  = d.hero_section;
const tr = d.trend_price_structure;
  const vl = d.volume_participation;
  const mo = d.momentum;
  const ri = d.volatility_risk;
  const kt = d.key_technical_levels;
  const eq = d.event_quality_engine;

  const pos = Number(h.daily_return_pct.value) >= 0;

  return (
    <Box sx={{ background: PAGE_BG, fontFamily: FONT, borderRadius: 3, overflow: "hidden" }}>

      {/* ─── HERO ──────────────────────────────────────────────────────── */}
      <Box sx={{ background: CARD, borderBottom: `1px solid ${BORDER}`, p: { xs: 2.5, md: 3.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 3 }}>

          {/* Left — identity + summary */}
          <Box sx={{ flex: 1, minWidth: 280 }}>
            {/* Company name — very large */}
            <Typography sx={{ fontSize: { xs: "1.6rem", md: "2.1rem" }, fontWeight: 900, color: T1, letterSpacing: "-0.025em", lineHeight: 1.1, mb: 2.5, fontFamily: FONT }}>
              {h.company_name.value}
            </Typography>

            {/* Summary from event engine */}
            <Typography sx={{ fontSize: "0.82rem", color: T1, lineHeight: 1.65, maxWidth: 560, fontWeight: 500, fontFamily: FONT }}>
              {eq.institutional_summary.summary.value}
            </Typography>
          </Box>

          {/* Right — price card + AI trend card */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>

            {/* Current Price */}
            <Box sx={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2, minWidth: 140, boxShadow: SHADOW }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.7rem", color: T3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: FONT }}>
                  Current Price
                </Typography>
                <Info d={h.current_price.description} u={h.current_price.use} />
              </Box>
              <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: T1, letterSpacing: "-0.02em", lineHeight: 1.1, fontFamily: FONT }}>
                ${fmt(h.current_price.value)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.6 }}>
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: pos ? GREEN : RED, fontFamily: FONT }}>
                  {pos ? "+" : ""}{h.daily_return_pct.value}% Today
                </Typography>
                <Info d={h.daily_return_pct.description} u={h.daily_return_pct.use} />
              </Box>
            </Box>

            {/* AI Trend Status */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${TEAL}18, ${PURPLE}18)`,
                border: `1px solid ${TEAL}33`,
                borderRadius: 2.5, p: 2, minWidth: 180,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontSize: "0.7rem", color: T2, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: FONT }}>
                  AI Trend Status
                </Typography>
                {h.ai_trend_status.description && <Info d={h.ai_trend_status.description} />}
              </Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: TEAL, lineHeight: 1.2, mb: 1, fontFamily: FONT }}>
                {h.ai_trend_status.signal.value}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: T1, fontWeight: 600, mb: 1.2, fontFamily: FONT }}>
                {h.ai_trend_status.confidence.value}% Confidence
              </Typography>
              <Box sx={{ height: 4, background: `${TEAL}20`, borderRadius: 99 }}>
                <Box sx={{ width: `${h.ai_trend_status.confidence.value}%`, height: "100%", background: `linear-gradient(90deg,${TEAL},${PURPLE})`, borderRadius: 99 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─── METRIC CARDS ROW ──────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3,1fr)" }, gap: 2, p: 2 }}>
        {/* <MetricCard
          label="AI Confidence"
          value={`${h.ai_trend_status.confidence.value}%`}
          sub={h.ai_trend_status.status.value}
          color={TEAL}
          d={h.ai_trend_status.description}
        /> */}
        <MetricCard
          label="Event Grade"
          value={h.event_grade.value}
          sub="Institutional Quality"
          color={PURPLE}
          d={h.event_grade.description}
          u={h.event_grade.use}
        />
        <MetricCard
          label="Momentum Score"
          value={h.momentum_status.score.value}
          sub={h.momentum_status.status.value}
          color={scCol(h.momentum_status.score.value)}
          d={h.momentum_status.description}
        />
        <MetricCard
          label="Relative Strength"
          value={h.relative_strength.score.value}
          sub={h.relative_strength.status.value}
          color={GREEN}
          d={h.relative_strength.description}
        />
      </Box>

      {/* ─── SECTION GRID ──────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, px: 2 }}>

        {/* Trend & Price Structure */}
        <Card title="Trend & Price Structure" subtitle="Institutional trend continuation diagnostics" desc={tr.description}>

          {/* OHLC row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", background: PAGE_BG, borderRadius: 2, border: `1px solid ${BORDER}`, mb: 2, overflow: "hidden" }}>
            <OHLCItem label="Open"  v={tr.analysis.open}  />
            <OHLCItem label="High"  v={tr.analysis.high}  />
            <OHLCItem label="Low"   v={tr.analysis.low}   />
            <OHLCItem label="Close" v={tr.analysis.close} />
          </Box>

          {/* High badges */}
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {tr.analysis.new_20d_high.value && (
              <Tooltip title={tr.analysis.new_20d_high.description || ""} arrow>
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: 99, background: `${TEAL}10`, border: `1px solid ${TEAL}44`, cursor: "default" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: TEAL, fontFamily: FONT }}>▲ 20D HIGH</Typography>
                </Box>
              </Tooltip>
            )}
            {tr.analysis.new_52w_high.value && (
              <Tooltip title={tr.analysis.new_52w_high.description || ""} arrow>
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: 99, background: `${GREEN}10`, border: `1px solid ${GREEN}44`, cursor: "default" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: GREEN, fontFamily: FONT }}>▲ 52W HIGH</Typography>
                </Box>
              </Tooltip>
            )}
          </Box>

          <DataRow label="Trend Status"     value={tr.signals.trend_status.value}        color={sigCol(tr.signals.trend_status.value)}        d={tr.signals.trend_status.description}        u={tr.signals.trend_status.use} />
          <DataRow label="Directional Bias" value={tr.signals.bullish_bearish_bias.value} color={sigCol(tr.signals.bullish_bearish_bias.value)} d={tr.signals.bullish_bearish_bias.description} u={tr.signals.bullish_bearish_bias.use} />
          <Box sx={{ mt: 2 }}>
            <GradBar label="Trend Continuation" value={tr.signals.trend_continuation_score.value}          d={tr.signals.trend_continuation_score.description}          u={tr.signals.trend_continuation_score.use} />
            <GradBar label="MA Alignment"        value={tr.signals.moving_average_alignment_quality.value} d={tr.signals.moving_average_alignment_quality.description} u={tr.signals.moving_average_alignment_quality.use} />
            <GradBar label="Breakout Quality"    value={tr.signals.breakout_quality.value}                 d={tr.signals.breakout_quality.description}                 u={tr.signals.breakout_quality.use} />
          </Box>
        </Card>

        {/* Volume & Participation */}
        <Card title="Volume & Participation" subtitle="Institutional liquidity & smart money flow" desc={vl.description}>
          <GradBar label="Participation Quality" value={vl.signals.participation_quality.value} d={vl.signals.participation_quality.description} u={vl.signals.participation_quality.use} />
          <GradBar label="Liquidity Quality"     value={vl.signals.liquidity_quality.value}     d={vl.signals.liquidity_quality.description}     u={vl.signals.liquidity_quality.use} />
          <DataRow label="Smart Money Activity"        value={vl.signals.smart_money_activity.value}           color={sigCol(vl.signals.smart_money_activity.value)}           d={vl.signals.smart_money_activity.description}           u={vl.signals.smart_money_activity.use} />
          <DataRow label="Accumulation / Distribution" value={vl.signals.accumulation_distribution_bias.value} color={sigCol(vl.signals.accumulation_distribution_bias.value)} d={vl.signals.accumulation_distribution_bias.description} u={vl.signals.accumulation_distribution_bias.use} />
        </Card>

        {/* Momentum Analysis */}
        <Card
          title="Momentum Analysis"
          subtitle="Breakout persistence & acceleration engine"
          // badge={`Score ${mo.signals.acceleration_score.value}`}
          badgeColor={TEAL}
          desc={mo.description}
        >
          <DataRow label="Momentum Status" value={mo.signals.momentum_status.value} color={sigCol(mo.signals.momentum_status.value)} d={mo.signals.momentum_status.description} u={mo.signals.momentum_status.use} />
          <Box sx={{ mt: 1.5 }}>
            <GradBar label="Breakout Continuation" value={mo.signals.breakout_continuation_quality.value} d={mo.signals.breakout_continuation_quality.description} u={mo.signals.breakout_continuation_quality.use} />
            <GradBar label="Acceleration"           value={mo.signals.acceleration_score.value}           d={mo.signals.acceleration_score.description}           u={mo.signals.acceleration_score.use} />
            <GradBar label="Pullback Quality"       value={mo.signals.pullback_quality.value}             d={mo.signals.pullback_quality.description}             u={mo.signals.pullback_quality.use} />
            <GradBar label="Persistence"            value={mo.signals.persistence_score.value}            d={mo.signals.persistence_score.description}            u={mo.signals.persistence_score.use} />
          </Box>
        </Card>

        {/* Volatility & Risk */}
        <Card
          title="Volatility & Risk"
          subtitle="Institutional risk management engine"
          // badge={`Risk ${ri.signals.risk_score.value}`}
          badgeColor={AMBER}
          desc={ri.description}
        >
          <DataRow label="Volatility Heat" value={ri.signals.volatility_heat.value} color={sigCol(ri.signals.volatility_heat.value)} d={ri.signals.volatility_heat.description} u={ri.signals.volatility_heat.use} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: `1px solid ${BORDER}`, mb: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.75rem", color: T3, fontWeight: 500, fontFamily: FONT }}>Speculative Activity</Typography>
              <Info d={ri.signals.speculative_activity_status.description} u={ri.signals.speculative_activity_status.use} />
            </Box>
            <Box sx={{ px: 1.2, py: 0.25, borderRadius: 99, background: `${AMBER}14`, border: `1px solid ${AMBER}44` }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: AMBER, fontFamily: FONT }}>{ri.signals.speculative_activity_status.value}</Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <GradBar label="Risk Score"             value={ri.signals.risk_score.value}             d={ri.signals.risk_score.description}             u={ri.signals.risk_score.use} />
            <GradBar label="Stabilization Score"    value={ri.signals.stabilization_score.value}    d={ri.signals.stabilization_score.description}    u={ri.signals.stabilization_score.use} />
            <GradBar label="Risk-Adjusted Momentum" value={ri.signals.risk_adjusted_momentum.value} d={ri.signals.risk_adjusted_momentum.description} u={ri.signals.risk_adjusted_momentum.use} />
          </Box>
        </Card>
      </Box>

      {/* ─── KEY TECHNICAL LEVELS ──────────────────────────────────────── */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Card title="Key Technical Levels" subtitle="Breakout zones, support & resistance mapping" desc={kt.description}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 2 }}>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
                <Typography sx={{ fontSize: "0.65rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Breakout Trigger</Typography>
                <Info d={kt.signals.breakout_trigger.description} u={kt.signals.breakout_trigger.use} />
              </Box>
              <Level value={kt.signals.breakout_trigger.value} color={BLUE} sub="Activate Above" />
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
                <Typography sx={{ fontSize: "0.65rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Resistance</Typography>
                <Info d={kt.signals.resistance_levels.description} u={kt.signals.resistance_levels.use} />
              </Box>
              {(Array.isArray(kt.signals.resistance_levels.value) ? kt.signals.resistance_levels.value : [kt.signals.resistance_levels.value]).map((r: number, i: number) => (
                <Level key={i} value={r} color={RED} />
              ))}
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
                <Typography sx={{ fontSize: "0.65rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Support</Typography>
                <Info d={kt.signals.support_levels.description} u={kt.signals.support_levels.use} />
              </Box>
              {(Array.isArray(kt.signals.support_levels.value) ? kt.signals.support_levels.value : [kt.signals.support_levels.value]).map((s: number, i: number) => (
                <Level key={i} value={s} color={GREEN} />
              ))}
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
                <Typography sx={{ fontSize: "0.65rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Support Quality</Typography>
                <Info d={kt.signals.trend_support_quality.description} u={kt.signals.trend_support_quality.use} />
              </Box>
              <Box sx={{ background: PAGE_BG, borderRadius: 2, border: `1px solid ${BORDER}`, p: 1.8 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
                  <Typography sx={{ fontSize: "0.7rem", color: T3, fontFamily: FONT }}>Score</Typography>
                  <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: scCol(kt.signals.trend_support_quality.value), fontFamily: "'SF Mono','Fira Code',monospace", letterSpacing: "-0.02em" }}>
                    {kt.signals.trend_support_quality.value}
                  </Typography>
                </Box>
                <Box sx={{ height: 6, background: BAR_TRACK, borderRadius: 99 }}>
                  <Box sx={{ width: `${kt.signals.trend_support_quality.value}%`, height: "100%", background: BAR_GRAD, borderRadius: 99 }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* ─── EVENT QUALITY ENGINE ──────────────────────────────────────── */}
      <Box sx={{ px: 2, mt: 2, pb: 2 }}>
        <Card title="Event Quality Engine" subtitle="Master AI institutional scoring system" desc={eq.description}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "140px 1fr 280px" }, gap: 3, alignItems: "start" }}>

            {/* Score circle */}
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                <Typography sx={{ fontSize: "0.65rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Overall Score</Typography>
                <Info d={eq.scores.overall_score.description} u={eq.scores.overall_score.use} />
              </Box>
              <Box sx={{ width: 90, height: 90, borderRadius: "50%", mx: "auto", border: `4px solid ${scCol(eq.scores.overall_score.value)}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: `${scCol(eq.scores.overall_score.value)}08`, boxShadow: `0 0 0 6px ${scCol(eq.scores.overall_score.value)}10`, mb: 1.5 }}>
                <Typography sx={{ fontSize: "1.7rem", fontWeight: 900, color: scCol(eq.scores.overall_score.value), fontFamily: FONT, lineHeight: 1 }}>
                  {eq.scores.overall_score.value}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ px: 2, py: 0.5, borderRadius: 1.5, background: `${AMBER}10`, border: `1px solid ${AMBER}44` }}>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, color: AMBER, fontFamily: FONT }}>
                    {eq.scores.institutional_grade.value}
                  </Typography>
                </Box>
                <Info d={eq.scores.institutional_grade.description} u={eq.scores.institutional_grade.use} />
              </Box>
            </Box>

            {/* Score bars */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "0 24px" }}>
              <GradBar label="Momentum Score" value={eq.scores.momentum_score.value} d={eq.scores.momentum_score.description} u={eq.scores.momentum_score.use} />
              <GradBar label="Liquidity Score" value={eq.scores.liquidity_score.value} d={eq.scores.liquidity_score.description} u={eq.scores.liquidity_score.use} />
              <GradBar label="Risk Score"      value={eq.scores.risk_score.value}      d={eq.scores.risk_score.description}      u={eq.scores.risk_score.use} />
              <GradBar label="Stability Score" value={eq.scores.stability_score.value} d={eq.scores.stability_score.description} u={eq.scores.stability_score.use} />
            </Box>

            {/* Institutional summary */}
            <Box sx={{ background: PAGE_BG, borderRadius: 2.5, border: `1px solid ${BORDER}`, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{ fontSize: "0.65rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>AI Conviction</Typography>
                  <Info d={eq.institutional_summary.ai_conviction.description} u={eq.institutional_summary.ai_conviction.use} />
                </Box>
                <Box sx={{ px: 1.2, py: 0.2, borderRadius: 99, background: `${sigCol(eq.institutional_summary.ai_conviction.value)}12`, border: `1px solid ${sigCol(eq.institutional_summary.ai_conviction.value)}40` }}>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: sigCol(eq.institutional_summary.ai_conviction.value), fontFamily: FONT }}>
                    {eq.institutional_summary.ai_conviction.value}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ fontSize: "0.77rem", color: T1, fontWeight: 500, lineHeight: 1.65, mb: 1.8, fontFamily: FONT }}>
                {eq.institutional_summary.summary.value}
              </Typography>

              <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.2, mb: 1.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.4 }}>
                  <Typography sx={{ fontSize: "0.63rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Trading Bias</Typography>
                  <Info d={eq.institutional_summary.trading_bias.description} u={eq.institutional_summary.trading_bias.use} />
                </Box>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: sigCol(eq.institutional_summary.trading_bias.value), fontFamily: FONT }}>
                  {eq.institutional_summary.trading_bias.value}
                </Typography>
              </Box>

              <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.4 }}>
                  <Typography sx={{ fontSize: "0.63rem", color: T3, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, fontFamily: FONT }}>Ideal Setup</Typography>
                  <Info d={eq.institutional_summary.ideal_setup.description} u={eq.institutional_summary.ideal_setup.use} />
                </Box>
                <Typography sx={{ fontSize: "0.75rem", color: T1, fontWeight: 500, lineHeight: 1.55, fontFamily: FONT }}>
                  {eq.institutional_summary.ideal_setup.value}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default QuantDashboardRenderer;
